import axios from 'axios'
import q from 'q'
import config from '@/config'
import accountConfig from 'account/config'
import lang from '@/lang'
import isElectron from '@/modules/_helpers/isElectron'
import { errorCodes } from '@/modules/base/util/constants'
import { toast } from '@/modules/base/components/toast/toastConfig'
import orgamax from 'ampersand-app' // only if you still need orgamax for other things
import { capturePostHogException } from '@/modules/posthog/posthog-tracking'
import { isRequestErrorIgnored } from '@/modules/posthog/error-tracking'

const enableLogging = false
/**
 * A simple dictionary to keep track of active requests by ID.
 * Key:   requestId (string)
 * Value: { cancel: Function, promise: Promise, requestHash: string }
 */
const inFlightRequests = {}

/**
 * Small helper for logging (optional).
 * Replace with your real logger if desired.
 */
const log = (msg) => {
  if (!enableLogging) return
  console.log(`${new Date().toISOString()}: ${msg}`)
}

/**
 * Generate a random hash (UUID v4 if available, else a random string).
 * This ensures each request has a 100% unique identifier.
 */
function generateRequestHash() {
  // Use the modern Web Crypto API if available (Node 19+, or most modern browsers).
  // Fallback to a quick random string if not available.
  if (typeof crypto !== 'undefined' && crypto?.randomUUID) {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).substring(2, 10)
}

/**
 * Main request function using Axios & Q, ensuring only one request
 * per requestId is in-flight at a time, and attaching a random
 * requestHash to each request.
 */
const request = function(endpoint, options) {
  if (!endpoint) {
    throw new TypeError('request invoked without `endpoint`!')
  }

  options = options || {}

  // We'll continue to return a Q-style deferred for compatibility
  const deferred = q.defer()

  // Default headers
  const defaultHeaders = typeof FormData !== 'undefined' && options.data instanceof FormData
    ? {}
    : { 'Content-Type': 'application/json' }

  // If the request is going to our known API, add the client header
  if (endpoint.startsWith(config.apiRoot)) {
    defaultHeaders[config.headers.apiClient] = isElectron()
      ? config.apiClients.electron
      : config.apiClients.web
  }

  // Build the axios config
  const axiosConfig = {
    method: (options.method || 'GET').toUpperCase(),
    url: endpoint,
    timeout: options.timeout || config.requestTimeout,
    headers: { ...defaultHeaders, ...options.headers },
    // We'll attach the cancelToken below if a requestId is provided
  }
  if (options.signal) {
    axiosConfig.signal = options.signal
  }
  if (options.params) {
    axiosConfig.params = options.params
  }

  // Token-based auth
  if (options.auth === true) {
    axiosConfig.headers.authorization = `Bearer ${orgamax.user.token}`
  }

  // For GET => use `params`; otherwise => use `data`
  if (axiosConfig.method === 'GET' && options.data) {
    axiosConfig.params = options.data
  } else if (options.data) {
    axiosConfig.data = options.data
  }

  // If a specific response type was requested
  if (options.responseType) {
    axiosConfig.responseType = options.responseType
  }

  // ------------------------------------------------------------------
  // Generate and attach a unique requestHash to each request
  // ------------------------------------------------------------------
  const requestHash = options.requestId && generateRequestHash()

  // ------------------------------------------------------------------
  // Concurrency handling by requestId
  // ------------------------------------------------------------------
  if (options.requestId) {
    // If there's an existing request, cancel it before creating a new one
    const existing = inFlightRequests[options.requestId]
    if (existing) {
      log(`Cancelling old request with ID: ${options.requestId}, and hash: ${inFlightRequests[options.requestId].requestHash} by ${requestHash}`)
      existing.cancel(
        `Cancelled due to a subsequent request with same requestId (${options.requestId}, with hash ${inFlightRequests[options.requestId].requestHash}) by ${requestHash}).`,
      )
      delete inFlightRequests[options.requestId]
    }

    // Create a new CancelToken for this request
    const cancelSource = axios.CancelToken.source()
    axiosConfig.cancelToken = cancelSource.token

    log(`Request started: ${options.requestId}, with hash: ${requestHash}`)
    // Pre-register this new request in the dictionary
    inFlightRequests[options.requestId] = {
      cancel: cancelSource.cancel,
      promise: null, // We'll set this once we create it
      requestHash, // Store the random hash for reference
    }
  }

  // ------------------------------------------------------------------
  // Perform the Axios request and attach the then/catch handlers
  // ------------------------------------------------------------------
  axios(axiosConfig)
    .then((response) => {
      // Cleanup concurrency dictionary
      if (options.requestId) {
        log(`Request done: ${options.requestId}, with hash: ${requestHash}, savedHash: ${inFlightRequests[options.requestId].requestHash}`)

        delete inFlightRequests[options.requestId]
      }

      // Check for 401 => logout logic
      if (
        !options.skipLogout
        && !orgamax.user?.loginInProgress
        && response.status === 401
        && response.request?.responseURL !== accountConfig.endpoints.login
      ) {
        return orgamax.user.logout()
      }

      // Check for 403 => show appropriate toast message
      if (response.status === 403) {
        if (response.data?.meta?.subscriptionModule?.[0]?.code === errorCodes.NOT_FOUND) {
          toast.showToast({
            type: 'error',
            message: lang.defaultForbiddenSubscriptionModuleMessage,
          })
        } else if (response.data?.meta?.email?.[0]?.code === errorCodes.NOT_CONFIRMED) {
          // DO-13065: sending mail is refused until the address is verified — one specific toast for
          // every send endpoint; the send masks additionally open the DOI modal (handleEmailNotConfirmedError)
          toast.showToast({
            type: 'error',
            message: lang.confirmEmail.sendEmailBlockedToast,
          })
        } else {
          toast.showToast({
            type: 'error',
            message: lang.defaultForbiddenMessage,
          })
        }
      }

      // If the status is < 400, treat it as success; otherwise reject
      if (response.status < 400) {
        deferred.resolve({
          response,
          requestHash,
          // legacy code, should be removed in the future
          body: response.data,
          statusCode: response.status,
        })
      } else {
        deferred.reject({
          response,
          requestHash,
          // legacy code, should be removed in the future
          body: response.data,
          statusCode: response.status,
        })
      }
    })
    .catch((error) => {
      if (error.response?.data?.name === 'DmsError') {
        toast.showToast({ type: 'error', message: lang.dmsError })
      }

      // Cleanup concurrency dictionary
      if (options.requestId) {
        log(`Request error/cancel: ${options.requestId}, with hash: ${requestHash}`)
      }

      // If this is an axios cancellation
      if (axios.isCancel(error)) {
        log(`Canceled ${options.requestId}, with hash: ${requestHash}: ${error.message}`)
        deferred.reject(new Error('Request canceled: ' + error.message))
        return
      }
      // Check for 401 => logout logic
      if (
        !options.skipLogout && !orgamax.user?.loginInProgress && error.response?.status === 401
          && error.request?.responseURL !== accountConfig.endpoints.login
      ) {
        if (orgamax.user.loggedIn) {
          toast.showToast({ title: lang.autoLogout.title, type: 'info', message: lang.autoLogout.message })
          return orgamax.user.logout()
        }
        return
      }
      // A real error (network, timeout, etc.)
      window.elasticApm?.captureError?.(new Error(`${endpoint} : ${error.message}`))
      // session/token endpoints fail by design with an expired token: not a bug, not an exception
      if (!isRequestErrorIgnored(endpoint)) {
        capturePostHogException(new Error(`${endpoint} : ${error.message}`), {
          source: 'request',
          endpoint,
          method: options.method || 'GET',
          statusCode: error.response?.status,
        })
      }
      deferred.reject({
        error,
        requestHash,
        // legacy code, should be removed in the future
        body: error.response?.data,
        statusCode: error.response?.status,
        response: error.response,
      })
    })

  // Return a Q promise for backward compatibility
  return deferred.promise
}

export default request
